import { onScopeDispose, watch, type Ref } from 'vue'
import type { Router } from 'vue-router'
import type { SortKey, StatusFilter } from '@/todos/listView'
import { readViewQuery, writeViewQuery } from '@/todos/viewQuery'

interface Options {
  status: Ref<StatusFilter>
  sort: Ref<SortKey>
  search: Ref<string>
  router: Router
  active?: () => boolean // (false while another page is being shown: its URL is none of our business)
  delay?: number // (how long the search rests before it goes into the URL)
}

// Keeps the status, the sort and the search of the list in step with the URL (`todos/viewQuery.ts`): the URL wins when the
// page opens and whenever it changes (a link, a bookmark, F5), for the keys it has; a change on the page replaces the
// URL (no history entry per click, the search after a pause in typing). Both directions compare before they write, so
// nothing loops.
export function useListViewQuery({
  status,
  sort,
  search,
  router,
  active = () => true,
  delay = 300,
}: Options): void {
  const query = () => router.currentRoute.value.query

  function applyUrl(): void {
    if (!active()) return
    const fromUrl = readViewQuery(query())
    if (fromUrl.status !== undefined && fromUrl.status !== status.value) status.value = fromUrl.status
    if (fromUrl.sort !== undefined && fromUrl.sort !== sort.value) sort.value = fromUrl.sort
    if (fromUrl.search !== undefined && fromUrl.search !== search.value.trim()) {
      search.value = fromUrl.search
    }
  }

  function writeUrl(): void {
    if (!active()) return
    const next = writeViewQuery(query(), {
      status: status.value,
      sort: sort.value,
      search: search.value,
    })
    const current = query()
    const same =
      Object.keys(next).length === Object.keys(current).length &&
      Object.entries(next).every(([key, value]) => JSON.stringify(current[key]) === JSON.stringify(value))
    if (!same) void router.replace({ query: next })
  }

  applyUrl()
  writeUrl() // (a stored preference the URL does not say yet, so a copy of the link carries the view)

  let timer: ReturnType<typeof setTimeout> | undefined
  watch([status, sort], writeUrl)
  watch(search, () => {
    clearTimeout(timer)
    timer = setTimeout(writeUrl, delay)
  })
  watch(() => router.currentRoute.value.fullPath, () => {
    applyUrl()
    writeUrl() // (another page of the same list: the view goes along into its URL)
  })
  onScopeDispose(() => clearTimeout(timer))
}
